/**
 * Copyright Highway9 Networks Inc.
 */

import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/store/hooks';
import { fetchRoles, selectRoles } from '~/store/context/rolesSlice';
import { roleStringFormatter } from '~/helpers/utils';
import type { Role } from '~/services/user-service';

export function useRoles() {
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectRoles);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const getRoleByName = useCallback(
    (roleName: string): Role | undefined => roles.find((r) => r.name === roleName),
    [roles]
  );

  const getRoleById = useCallback(
    (roleId: string): Role | undefined => roles.find((r) => r.id === roleId),
    [roles]
  );

  const getRoleDisplayName = useCallback(
    (roleName: string): string => getRoleByName(roleName)?.displayName || roleStringFormatter(roleName),
    [getRoleByName]
  );

  // MCS-3799: prefer `displayRole` (set by the backend for partner users whose effective
  // `roles` was rewritten to RAN_KPI_ONLY) over `roles`.
  const getUserRoleDisplay = useCallback(
    (user: { roles?: string[]; displayRole?: string[] } | null | undefined): string => {
      if (!user) return "";
      const source = user.displayRole?.length ? user.displayRole : (user.roles ?? []).map(getRoleDisplayName);
      return source.join(", ");
    },
    [getRoleDisplayName]
  );

  return { roles, getRoleByName, getRoleById, getRoleDisplayName, getUserRoleDisplay };
}
